// ═══════════════════════════════════════════════════════════════
// PACE — Printable Report Exports
// ═══════════════════════════════════════════════════════════════

import type { PaceTier, PaceProfile } from '../store/paceStore';

const PACE_TIER_LABELS: Record<PaceTier, string> = {
  primary: 'Primary', alternate: 'Alternate', contingent: 'Contingent', emergency: 'Emergency',
};
const COMM_METHOD_LABELS: Record<string, string> = {
  cell_phone: 'Cell Phone', landline: 'Landline', satellite_phone: 'Satellite Phone',
  radio: 'Radio / HAM', email: 'Email', text_sms: 'Text / SMS', signal: 'Signal',
  teams: 'Microsoft Teams', zoom: 'Zoom', in_person: 'In-Person Meeting Point',
  runners: 'Runners / Couriers', other: 'Other',
};

function openPrintWindow(title: string, html: string) {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!DOCTYPE html>
<html><head><title>${title}</title>
<style>
  @page { margin: 0.75in; size: letter; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', -apple-system, sans-serif; color: #112D4E; font-size: 11px; line-height: 1.4; }
  h1 { font-size: 18px; margin-bottom: 4px; }
  h2 { font-size: 14px; margin: 16px 0 8px; color: #3F72AF; }
  .header-bar { background: #112D4E; color: #fff; padding: 12px 16px; margin: -4px -4px 16px; border-radius: 4px; display: flex; justify-content: space-between; align-items: center; }
  .header-bar h1 { color: #fff; font-size: 16px; }
  .header-bar .date { color: rgba(255,255,255,.6); font-size: 10px; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 16px; }
  th { background: #f1f5f9; text-align: left; padding: 6px 8px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; border-bottom: 2px solid #e2e8f0; }
  td { padding: 6px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  tr:nth-child(even) td { background: #f9fafb; }
  .tier-badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 700; font-size: 10px; }
  .tier-primary { background: #dcfce7; color: #166534; }
  .tier-alternate { background: #dbeafe; color: #1e40af; }
  .tier-contingent { background: #fef9c3; color: #854d0e; }
  .tier-emergency { background: #fee2e2; color: #991b1b; }
  .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
  .stat-card { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; text-align: center; }
  .stat-card .value { font-size: 20px; font-weight: 700; }
  .stat-card .label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: .3px; }
  .progress-bar { height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; margin: 4px 0; }
  .progress-fill { height: 100%; border-radius: 3px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>${html}</body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 500);
}

export function exportComplianceReport(
  incidentName: string,
  compliance: {
    totalProfiles: number;
    compliantProfiles: number;
    compliancePercent: number;
    staleProfiles: number;
    byTier: { tier: PaceTier; count: number; percent: number }[];
    byDepartment: { department: string; total: number; compliant: number; percent: number }[];
  },
) {
  const tierColors: Record<string, string> = {
    primary: '#16a34a', alternate: '#2563eb', contingent: '#ca8a04', emergency: '#dc2626',
  };
  const html = `
    <div class="header-bar">
      <h1>PACE Compliance Report — ${incidentName}</h1>
      <span class="date">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
    </div>
    <div class="stat-grid">
      <div class="stat-card"><div class="value">${compliance.totalProfiles}</div><div class="label">Total Profiles</div></div>
      <div class="stat-card"><div class="value" style="color:#16a34a">${compliance.compliantProfiles}</div><div class="label">Compliant</div></div>
      <div class="stat-card"><div class="value" style="color:#3F72AF">${compliance.compliancePercent}%</div><div class="label">Compliance Rate</div></div>
      <div class="stat-card"><div class="value" style="color:#d97706">${compliance.staleProfiles}</div><div class="label">Stale Records</div></div>
    </div>
    <h2>Coverage by PACE Tier</h2>
    ${compliance.byTier.map(t => `
      <div style="margin-bottom:8px">
        <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:2px">
          <span><span class="tier-badge tier-${t.tier}">${PACE_TIER_LABELS[t.tier]}</span></span>
          <span>${t.count}/${compliance.totalProfiles} (${t.percent}%)</span>
        </div>
        <div class="progress-bar"><div class="progress-fill" style="width:${t.percent}%;background:${tierColors[t.tier]}"></div></div>
      </div>
    `).join('')}
    ${compliance.byDepartment.length > 0 ? `
    <h2>Compliance by Department</h2>
    <table>
      <thead><tr><th>Department</th><th>Members</th><th>Compliant</th><th>Rate</th></tr></thead>
      <tbody>
        ${compliance.byDepartment.map(d => `
          <tr>
            <td><strong>${d.department}</strong></td>
            <td>${d.total}</td>
            <td>${d.compliant}</td>
            <td><strong style="color:${d.percent >= 80 ? '#16a34a' : d.percent >= 50 ? '#d97706' : '#dc2626'}">${d.percent}%</strong></td>
          </tr>
        `).join('')}
      </tbody>
    </table>` : ''}
    <div style="margin-top:24px;padding:8px;background:#f8fafc;border-radius:4px;font-size:9px;color:#94a3b8;text-align:center">
      Generated by OpenGov PACE &middot; ${new Date().toISOString()}
    </div>`;
  openPrintWindow(`PACE Compliance Report - ${incidentName}`, html);
}

export function exportContactSheet(
  incidentName: string,
  profiles: PaceProfile[],
) {
  const tiers: PaceTier[] = ['primary', 'alternate', 'contingent', 'emergency'];
  const html = `
    <div class="header-bar">
      <h1>PACE Contact Sheet — ${incidentName}</h1>
      <span class="date">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
    </div>
    <p style="color:#64748b;font-size:11px;margin-bottom:16px">${profiles.length} profiles &middot; Print and keep accessible for call-down operations</p>
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th style="color:#166534">Primary</th>
          <th style="color:#1e40af">Alternate</th>
          <th style="color:#854d0e">Contingent</th>
          <th style="color:#991b1b">Emergency</th>
        </tr>
      </thead>
      <tbody>
        ${profiles.map(p => `
          <tr>
            <td>
              <strong>${p.personName}</strong>
              ${p.department ? `<br><span style="color:#94a3b8;font-size:9px">${p.department}</span>` : ''}
            </td>
            ${tiers.map(t => {
              if (p.tier !== t) return '';
              return `<td>
                <span style="font-size:9px;color:#64748b">${COMM_METHOD_LABELS[p.method] || p.method}</span><br>
                <strong>${p.contactValue}</strong>
                ${p.backupValue ? `<br><span style="font-size:9px;color:#94a3b8">Alt: ${p.backupValue}</span>` : ''}
              </td>`;
            }).join('') || tiers.map(() => '<td style="color:#cbd5e1">&mdash;</td>').join('')}
          </tr>
        `).join('')}
      </tbody>
    </table>
    <div style="margin-top:16px;padding:8px;background:#f8fafc;border-radius:4px;font-size:9px;color:#94a3b8;text-align:center">
      CONFIDENTIAL &mdash; For authorized personnel only &middot; Generated by OpenGov PACE &middot; ${new Date().toISOString()}
    </div>`;
  openPrintWindow(`PACE Contact Sheet - ${incidentName}`, html);
}
